import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Row, Col, Card, Collapse, Form, Container } from 'react-bootstrap';
import { SingleSelectAsync, SelectOption } from './SIngleSelectAsync';
import FeatureNav from './FeatureNav';
import FooterSection from './FooterSection';
import HeaderComponent from './HeaderComponent';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface AllEntity {
  entity_name: string;
  entity_id: number;
}

interface VendorPayment {
  entity_id: number;
  fiscal_year: number;
  entity_name: number;
  vendor_name: string;
  ven_rank: string;
  net_amount: number;
}

interface VendorPayee {
  entity_id: number;
  fiscal_year: number;
  entity_name: number;
  vendor_name: string;
  ven_rank: string;
  net_amount: number;
}

interface FormOptions {
  value: string;
  label: string;
}

const EntityHighestPaidVendors = () => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number | null>(null);
  const [entityNameOptions, setEntityNameOptions] = useState<FormOptions[]>([]);
  const [allEntities, setAllEntities] = useState<AllEntity[]>([]);
  const [allFiscalYears, setAllFiscalYears] = useState<string[] | null>(null);
  const [masterVendorPaymentData, setMasterVendorPaymentData] = useState<VendorPayment[]>([]);
  const [masterVendorPayeeData, setMasterVendorPayeeData] = useState<VendorPayee[]>([]);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
	const tabulatorRef2 = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef2 = useRef<HTMLDivElement>(null);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const [loading, setLoading] = useState<boolean>(false);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedEntityName = selectedEntityName?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName}_highest_paid_vendors.${type}`);
  };

  const memoizedEntityNameOptions = useMemo(() => {
    if (!allEntities) return [];

    const filteredEntities = allEntities.filter(entity => entity.entity_name !== "-- Select --")

    const rawNames = filteredEntities.map(entity => String(entity.entity_name || entity));

    const uniqueNames = [...new Set(rawNames)];

    uniqueNames.sort((a, b) => a.localeCompare(b));

    return uniqueNames.map(name => ({
      value: name,
      label: name
    }));
  }, [allEntities]);

  const loadEntityOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    if (!inputValue) return memoizedEntityNameOptions;

    const search = inputValue.toLowerCase();
    return memoizedEntityNameOptions.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }, [memoizedEntityNameOptions]);

  const memoizedFiscalYearOptions = useMemo(() => {
    if (!allFiscalYears) return [];

    return [...allFiscalYears]
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      .map(year => ({
        value: String(year),
        label: String(year)
      }));
  }, [allFiscalYears]);
  
  const loadFiscalYearOptions = async (inputValue: string): Promise<SelectOption[]> => {
    if (inputValue) {
      return memoizedFiscalYearOptions.filter(option =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    return memoizedFiscalYearOptions;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    await fetchHighestPaidVendorsData();
		await fetchHighestPaidVendorPayeeData();
  };

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string) => {
    return `${apiEndpointUrl}${searchParameter}`;
  };

  const getFormOptions = (data: any[], key: string) => {
    const unique = [...new Set(data.map((item: any) => item[key]?.toString()))]
      .filter(val => {
        if (!val) return false;
        return !/select/i.test(val);
      })
      .sort((a, b) => {
        const aNum = Number(a);
        const bNum = Number(b);
        return (!isNaN(aNum) && !isNaN(bNum)) ? aNum - bNum : a.localeCompare(b);
      });

    return unique.map(val => ({ value: val, label: val }));
  };

  const getAllEntitiesAndFiscalYears = async(): Promise<void> => {
    try {
			const searchString = `?function=getAvailableEntitiesByYearRVEX&parameter=%7B%7D`;
      const res = await fetch(buildApiUrl(apiEndpoint, searchString));
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = res.ok ? await res.json(): [];

      const entityNamesAndIds = data.filter((entity: { entity_name: string; entity_id: number }) => 
        entity.entity_name && entity.entity_id
      );

      const entities = getFormOptions(data, "entity_name");
      setEntityNameOptions(entities);

      const getFiscalYearFormOptions = (data: any[], key: string) => {
        const unique = [...new Set(data.map(item => item[key]?.toString()))]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        return unique.map(val => ({
          value: val,
          label: val
        }));
      }

      const fiscalYears = getFiscalYearFormOptions(data, "fiscal_year");
      const fiscalYearArray = fiscalYears
        .map(fiscalYear => String(fiscalYear.value))
        .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

      setAllEntities(entityNamesAndIds);
      setAllFiscalYears(fiscalYearArray);

    } catch (error: any) {
      
    } finally {
      setLoading(false);
    }
  }

	// Highest Paid Vendors
  const fetchHighestPaidVendorsData = async () => {
    if (!selectedEntityName || !selectedFiscalYear) {
      setLoading(false);
      return;
    }

    const selectedMatchedEntity = allEntities.find(entity => entity.entity_name === selectedEntityName);
    const entityId = selectedMatchedEntity ? selectedMatchedEntity.entity_id.toString() : null;

    if (!entityId) return;

    setLoading(true);

    try {
      const payload = {
        entity_name: entityId,
        fiscal_year: selectedFiscalYear.toString(),
      }

      const encodedParam = encodeURIComponent(JSON.stringify(payload));
      const highestVendorPaymentsSearchString = `?function=getHighestPayments&parameter=${encodedParam}`;

      const res = await fetch(buildApiUrl(apiEndpoint, highestVendorPaymentsSearchString));
			const highestVendorPaymentsData = res.ok ? await res.json() : [];

			setMasterVendorPaymentData(highestVendorPaymentsData);
      
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  }

	// Highest Paid Vendor Payees
	const fetchHighestPaidVendorPayeeData = async () => {
    if (!selectedEntityName || !selectedFiscalYear) {
      setLoading(false);
      return;
    }

    const selectedMatchedEntity = allEntities.find(entity => entity.entity_name === selectedEntityName);
    const entityId = selectedMatchedEntity ? selectedMatchedEntity.entity_id.toString() : null;

    if (!entityId) return;
    setLoading(true);

    try {
      const payload = {
        entity_name: entityId,
        fiscal_year: selectedFiscalYear.toString(),
      }

      const encodedParam = encodeURIComponent(JSON.stringify(payload));
      const highestVendorPayeeSearchString = `?function=getHighestPaidVendors&parameter=${encodedParam}`;

			const res = await fetch(buildApiUrl(apiEndpoint, highestVendorPayeeSearchString));
      const highestVendorPayeeData = res.ok ? await res.json() : [];

			setMasterVendorPayeeData(highestVendorPayeeData);
      
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  }

	// Moved from inside the useEffect statement - Create Largest Single Payments Table
	const createPaymentsTable = async () => {
		const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;
    if (!tableRef.current) return;

		tabulatorRef.current = new Tabulator(tableRef.current, {
			data: masterVendorPaymentData,
			minHeight: "450px",
			maxHeight: "100%",
			layout: isMobile ? "fitDataFill" : "fitColumns",
			pagination: "local",
			paginationSize: 10,
			headerSort: true,
			initialSort: [
				{
					column: "ven_rank",
					dir: "asc",
				}
			],
			columns: [
				{
					titleFormatter: () => {
						const el = document.createElement("span");
						el.innerHTML = "<i class='fas fa-trophy'></i> Rank";
						return el;
					},
					title: "Rank",
					field: "ven_rank",
					sorter: "number",
				},
				{
					titleFormatter: () => {
						const el = document.createElement("span");
						el.innerHTML = "<i class='fas fa-user'></i> Vendor Name";
						return el;
					},
					title: "Vendor Name",
					field: "vendor_name",
				},
				{
					titleFormatter: () => {
						const element = document.createElement("span");
						element.innerHTML = "<i class='fas fa-dollar-sign'></i> Net Amount";
						return element;
					},
					field: "amount",
					formatter: "money",
					sorter: "number",
					titleDownload: "Net Amount",
					formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
				},
				{
					titleFormatter: () => {
						const element = document.createElement("span");
						element.innerHTML = "<i class='fas fa-calendar-alt'></i> Fiscal Year";
						return element;
					},
					field: "fiscal_year",
					titleDownload: "Fiscal Year",
				},
				{
					titleFormatter: () => {
						const element = document.createElement("span");
						element.innerHTML = "<i class='fas fa-landmark'></i> Entity";
						return element;
					},
					field: "entity_name",
					titleDownload: "Entity",
				},
			],
		})
	}

	// Moved from inside the useEffect statement - Create Largest Single Payees Table
  const createPayeesTable = async () => {
		const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;
    if (!tableRef2.current) return; 

		tabulatorRef2.current = new Tabulator(tableRef2.current, {
			data: masterVendorPayeeData,
			minHeight: "450px",
			maxHeight: "100%",
			layout: isMobile ? "fitDataFill" : "fitColumns",
			pagination: "local",
			paginationSize: 10,
			placeholder: "No Data Available",
			headerSort: true,
			initialSort: [
				{
					column: "ven_rank",
					dir: "asc",
				}
			],
			columns: [
				{
					titleFormatter: () => {
						const el = document.createElement("span");
						el.innerHTML = "<i class='fas fa-trophy'></i> Rank";
						return el;
					},
					title: "Rank",
					field: "ven_rank",
          sorter: "number",
				},
				{
					titleFormatter: () => {
						const el = document.createElement("span");
						el.innerHTML = "<i class='fas fa-user'></i> Vendor Name";
						return el;
					},
					title: "Vendor Name",
					field: "vendor_name",
				},
        {
					titleFormatter: () => {
						const element = document.createElement("span");
						element.innerHTML = "<i class='fas fa-dollar-sign'></i> Net Amount";
						return element;
					},
          title: "Net Amount",
          field: "net_amount",
					formatter: "money",
					sorter: "number",
					titleDownload: "Net Amount",
					formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
				},
				{
					titleFormatter: () => {
						const element = document.createElement("span");
						element.innerHTML = "<i class='fas fa-calendar-alt'></i> Fiscal Year";
						return element;
					},
					field: "fiscal_year",
					titleDownload: "Fiscal Year",
				},
				{
					titleFormatter: () => {
						const element = document.createElement("span");
						element.innerHTML = "<i class='fas fa-landmark'></i> Entity";
						return element;
					},
					field: "entity_name",
					titleDownload: "Entity",
				},
			],
		})
	}

	// Added to ensure both tabulator tables load simultaneously
	const bothTablesReady = selectedEntityName && 
    (masterVendorPaymentData?.length ?? 0) > 0 && 
    (masterVendorPayeeData?.length ?? 0) > 0;

  // Get All Entities and Fiscal Years
  useEffect(() => {
    getAllEntitiesAndFiscalYears();
  }, []);

	// Initialize Largest Single Payments and Largest Single Payees Tabulator Tables
	useEffect(() => {
    console.log(`Memoized entity name options: ${JSON.stringify(memoizedEntityNameOptions)}`);
    if (!bothTablesReady) return;

    setLoading(true);
    const controller = new AbortController();

    if (tabulatorRef.current) {
        tabulatorRef.current.destroy();
        tabulatorRef.current = null;
    }
    if (tabulatorRef2.current) {
        tabulatorRef2.current.destroy();
        tabulatorRef2.current = null;
    }

    // Initialize Tables
    createPaymentsTable();
    createPayeesTable();

    const timer = setTimeout(() => {
      if (tabulatorRef.current) tabulatorRef.current.redraw(true);
      if (tabulatorRef2.current) tabulatorRef2.current.redraw(true);
      setLoading(false); 
    }, 200);

    const handleResize = () => {
      const newLayout = window.innerWidth < 1025 ? "fitDataFill" : "fitColumns";
      if (tabulatorRef.current) {
          tabulatorRef.current.setOptions({ layout: newLayout });
          tabulatorRef.current.redraw(true);
      }
      if (tabulatorRef2.current) {
          tabulatorRef2.current.setOptions({ layout: newLayout });
          tabulatorRef2.current.redraw(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      controller.abort();
      window.removeEventListener("resize", handleResize);

      if (tabulatorRef.current) {
          tabulatorRef.current.destroy();
          tabulatorRef.current = null;
      }

      if (tabulatorRef2.current) {
          tabulatorRef2.current.destroy();
          tabulatorRef2.current = null;
      }
    };
	}, [masterVendorPaymentData, masterVendorPayeeData, bothTablesReady]);

  return (
    <>
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      <Container fluid as="main">
        <Row className="mb-4 px-4">
          <Col xs={12} className="px-0">
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center", textAlign: "center" }}>
                <section>
                  <div className="tu_tabHeader">
                    <div className="tu-tabHeader__titleRow">
                      <h1 className="tu-tabHeader__title">
                      Highest Paid Vendors</h1>
                        <button
                        type="button"
                        className="tu-tabHeader__infoBtn"
                        onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                        aria-label="Highest Paid Vendors info"
                      >
                        <i className="fas fa-info"/>
                        </button>
                      </div>
                  </div>
                  <Collapse in={showHeaderInfo}>
                    <div>
                    <p className="lead mt-2 text-center">
                      To search for highest paid vendors, select an entity and then a fiscal year. Click the submit button and the top 100 vendors will be returned. The top 100 highest vendor payments are also included in a separate table.
                    </p>
                    </div>
                  </Collapse>
                </section>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Loader */}
        {loading ? <Loader /> : <></>}

        {/* Search Form */}
        <Form onSubmit={handleSubmit}>
          <Row className="px-4 mb-1">
            <Card className="px-0">
              <Card.Header className="bg-secondary text-white" style={{ border: "none"}}>
                <h3 className="text-white">Search</h3>
              </Card.Header>
              <Card.Body className="p-2">
                <Row>
                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Select an Entity
                    </h5>
                      <SingleSelectAsync
                        value={selectedEntityName ? { label: selectedEntityName, value: selectedEntityName } : null}
                        onChange={(newValue) => setSelectedEntityName(newValue ? newValue.value : null)}
                        loadOptions={loadEntityOptions}
                        defaultOptions={memoizedEntityNameOptions.length > 0 ? memoizedEntityNameOptions : true} 
                        type="entity"
                      />
                  </Col>

                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Select a Fiscal Year
                    </h5>
                      <SingleSelectAsync
                        value={selectedFiscalYear ? { label: String(selectedFiscalYear), value: String(selectedFiscalYear) } : null}
                        key={memoizedFiscalYearOptions.length}
                        onChange={(newValue) => {
                          const val = newValue ? Number(newValue.value) : null;
                          setSelectedFiscalYear(val);
                        }}
                        loadOptions={loadFiscalYearOptions}
                        type="title"
                        defaultOptions={memoizedFiscalYearOptions}
                      />
                  </Col>

                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <button
                      type="submit"
                      className="btn btn-secondary"
                      aria-label="Submit"
                      onClick={handleSubmit}
                      style={{marginTop: window.innerWidth > 768 ? "32.5px" : "10px"}}
                    >
                      <i className="fas fa-paper-plane"></i>{" "} Submit
                    </button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Row>
        </Form>

        {/* Data Section */}
        <Row className="py-4 px-4">
					{/* Largest Single Payments */}
          <Col xs={12} sm={12} md={12} lg={6} xl={6} 
	          className={ window.innerWidth > 768 ? "" : "px-0"}  
	          style={{
	            marginBottom: window.innerWidth > 768 ? "0": "20px",
	            paddingLeft: window.innerWidth > 768 ? "0" : "inherit"
            }}
          >
            <Card className="bg-white">
              <Card.Header className="bg-secondary">
								<h3 className="text-white">Largest Single Payments</h3>
							</Card.Header>
							<Card.Body>
                <section>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div style={{ width: "100%",
                    position: "relative"
                  }}>

                    {/* No Data Available */}
                    {!loading && (!masterVendorPaymentData || masterVendorPaymentData.length === 0) && (
                      <Card.Text className="text-center py-5">No Data available - please select a new entity</Card.Text>
                    )}

                    {/* Table Container - Rendered but hidden if not needed */}
                    <div 
                      ref={tableRef} 
                      className={`table-light ${bothTablesReady ? 'd-block' : 'd-none'}`}  
                    />
                  </div>
                </section>
							</Card.Body>
            </Card>
          </Col>

					<Col xs={12} sm={12} md={12} lg={6} xl={6} 
            className={ window.innerWidth > 768 ? "" : "px-0"}  
            style={{
              paddingRight: window.innerWidth > 768 ? "0" : "inherit"
            }}
          >
            <Card className="bg-white">
              <Card.Header className="bg-secondary">
								<h3 className="text-white">Largest Single Payees</h3>
							</Card.Header>
							<Card.Body>
                <section>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div style={{ width: "100%",
                    position: "relative"
                  }}>

                    {/* No Data Available */}
                    {!loading && (!masterVendorPayeeData || masterVendorPayeeData.length === 0) && (
                      <Card.Text className="text-center py-5">No Data available - please select new entity</Card.Text>
                    )}

                    {/* Table Container - Rendered but hidden if not needed */}
                    <div 
                      ref={tableRef2} 
                      className={`table-light ${bothTablesReady ? 'd-block' : 'd-none'}`}  
                      style={{ minHeight: 400 }}  
                    />
                  </div>
                </section>
							</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <FooterSection/>
    </>
  )
}

export default EntityHighestPaidVendors;